import { useState } from "react";
import client from "../api/client";

interface Props {
	onLogin: () => void;
}

export function LoginPage({ onLogin }: Props) {
	const [userName, setUserName] = useState("");
	const [password, setPassword] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		setError(null);
		setLoading(true);
		if (userName === "demo" && password === "demo") {
			localStorage.setItem("demoMode", "true");
			location.reload();
			return;
		}
		try {
			await client.login(userName, password);
			onLogin();
		} catch (err) {
			setError(err instanceof Error ? err.message : "Login failed");
		} finally {
			setLoading(false);
		}
	}

	return (
		<div
			className="flex flex-col min-h-[100dvh] overflow-hidden relative"
			style={{ background: "#111" }}
		>
			{/* Subtle diagonal grid texture */}
			<div
				className="absolute inset-0 pointer-events-none"
				style={{
					backgroundImage:
						"repeating-linear-gradient(45deg, rgba(200,16,46,0.04) 0, rgba(200,16,46,0.04) 1px, transparent 0, transparent 50%)",
					backgroundSize: "24px 24px",
				}}
			/>

			{/* Red hero with diagonal cut */}
			<div
				className="relative"
				style={{
					background: "linear-gradient(150deg, #c8102e 0%, #7a0018 100%)",
					clipPath: "polygon(0 0, 100% 0, 100% 80%, 0 100%)",
					minHeight: "30vh",
				}}
			/>

			{/* Form */}
			<div className="flex-1 flex flex-col justify-center px-7 -mt-10 relative z-10 pb-12">
				<img
					src="/logo.svg"
					alt="QuikTrip"
					className="w-16 h-16 object-contain mb-6 rounded-xl shadow-lg"
				/>
				<h1 className="text-white text-4xl font-black tracking-tight leading-none mb-1">
					Sign in
				</h1>
				<p className="text-white/30 text-sm mb-6">
					Enter your QT credentials to continue
				</p>

				<div
					className="rounded-xl px-4 py-3 mb-8 text-sm"
					style={{
						background: "rgba(255,255,255,0.05)",
						border: "1px solid rgba(255,255,255,0.09)",
					}}
				>
					<span className="text-white/40 uppercase tracking-[0.15em] text-[10px] font-semibold block mb-1">Demo</span>
					<span className="text-white/60">Use username </span>
					<code className="text-white font-mono">demo</code>
					<span className="text-white/60"> and password </span>
					<code className="text-white font-mono">demo</code>
        </div>

				<form onSubmit={handleSubmit} className="flex flex-col gap-4">
					<div className="flex flex-col gap-1.5">
						<label
							htmlFor="username"
							className="text-white/40 text-[10px] uppercase tracking-[0.2em] font-semibold"
						>
							Username
						</label>
						<input
							id="username"
							type="text"
							placeholder="Your username"
							value={userName}
							onChange={(e) => setUserName(e.target.value)}
							autoComplete="username"
							required
							className="rounded-xl px-4 py-3.5 text-white text-sm placeholder-white/20 focus:outline-none transition-colors"
							style={{
								background: "rgba(255,255,255,0.05)",
								border: "1px solid rgba(255,255,255,0.09)",
							}}
							onFocus={(e) => {
								e.currentTarget.style.border = "1px solid rgba(200,16,46,0.5)";
								e.currentTarget.style.background = "rgba(255,255,255,0.07)";
							}}
							onBlur={(e) => {
								e.currentTarget.style.border =
									"1px solid rgba(255,255,255,0.09)";
								e.currentTarget.style.background = "rgba(255,255,255,0.05)";
							}}
						/>
					</div>

					<div className="flex flex-col gap-1.5">
						<label
							htmlFor="password"
							className="text-white/40 text-[10px] uppercase tracking-[0.2em] font-semibold"
						>
							Password
						</label>
						<input
							id="password"
							type="password"
							placeholder="Your password"
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							autoComplete="current-password"
							required
							className="rounded-xl px-4 py-3.5 text-white text-sm placeholder-white/20 focus:outline-none transition-colors"
							style={{
								background: "rgba(255,255,255,0.05)",
								border: "1px solid rgba(255,255,255,0.09)",
							}}
							onFocus={(e) => {
								e.currentTarget.style.border = "1px solid rgba(200,16,46,0.5)";
								e.currentTarget.style.background = "rgba(255,255,255,0.07)";
							}}
							onBlur={(e) => {
								e.currentTarget.style.border =
									"1px solid rgba(255,255,255,0.09)";
								e.currentTarget.style.background = "rgba(255,255,255,0.05)";
							}}
						/>
					</div>

					{error && (
						<div
							className="rounded-xl px-4 py-3 text-sm"
							style={{
								background: "rgba(200,16,46,0.12)",
								border: "1px solid rgba(200,16,46,0.25)",
								color: "#ff6b6b",
							}}
						>
							{error}
						</div>
					)}

					<button
						type="submit"
						disabled={loading}
						className="mt-2 rounded-xl py-4 text-white font-bold text-sm tracking-wide transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center"
						style={{
							background: "linear-gradient(145deg, #c8102e 0%, #8b0010 100%)",
						}}
					>
						{loading ? (
							<span className="loading loading-spinner loading-sm" />
						) : (
							"Sign In"
						)}
					</button>
				</form>
			</div>
		</div>
	);
}
